import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import { Box, CircularProgress, Grid2, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormStyles } from "../../hooks/useFormStyles";
import {
  getUpdateAccountFormSchema,
  UpdateAccountForm,
} from "../../types/forms/admin/account";
import { Account, UpdateAccountRequest } from "../../types/model/account";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { AccountType } from "../../types/account";

interface AccountInfoProps {
  accountData: Account | undefined;
  onSubmit: (values: UpdateAccountRequest) => Promise<void>;
}

export default function AccountInfo({
  accountData,
  onSubmit,
}: Readonly<AccountInfoProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getUpdateAccountFormSchema(t);

  const methods = useForm<UpdateAccountForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: accountData?.name ?? "",
      type: accountData?.type ?? undefined,
      email: accountData?.email ?? "",
      phoneNumber: accountData?.phoneNumber ?? "",
      fiscalNumber: accountData?.fiscalNumber ?? "",
      address: accountData?.address ?? "",
      zipCode: accountData?.zipCode ?? "",
      city: accountData?.city ?? "",
      description: accountData?.description ?? "",
    },
  });

  const handleFormSubmit = async (values: UpdateAccountForm) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (accountData) {
      methods.reset({
        ...accountData,
      });
    }
  }, [accountData]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h5">
                {t("admin.accounts.account.title")}
              </Typography>

              {/* Action Icons */}
              {isEditing ? ( 
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("generic.save")}>
                    <IconButton
                      color="success"
                      onClick={methods.handleSubmit(handleFormSubmit)}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("generic.cancel")}>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setIsEditing(false);
                        methods.reset();
                      }}
                    >
                      <EditOffIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Tooltip title={t("generic.edit")}>
                  <IconButton
                    color="primary"
                    onClick={() => setIsEditing(true)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={`${t("admin.accounts.account.name")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("name")}
              error={!!methods.formState.errors.name}
              helperText={methods.formState.errors.name?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="type"
              control={methods.control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label={`${t("admin.accounts.account.type")} *`}
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing}
                  sx={{ ...formStyles }}
                  {...field}
                  error={!!methods.formState.errors.type}
                  helperText={methods.formState.errors.type?.message}
                >
                  {Object.values(AccountType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("admin.accounts.account.fiscal-number")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("fiscalNumber")}
              error={!!methods.formState.errors.fiscalNumber}
              helperText={methods.formState.errors.fiscalNumber?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={`${t("admin.accounts.account.email")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("email")}
              error={!!methods.formState.errors.email}
              helperText={methods.formState.errors.email?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("admin.accounts.account.phone-number")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("phoneNumber")}
              error={!!methods.formState.errors.phoneNumber}
              helperText={methods.formState.errors.phoneNumber?.message}
            />
          </Grid2>
          <Grid2 size={7}>
            <TextField
              fullWidth
              label={t("admin.accounts.account.address")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("address")}
              error={!!methods.formState.errors.address}
              helperText={methods.formState.errors.address?.message}
            />
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              label={t("admin.accounts.account.city")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("city")}
              error={!!methods.formState.errors.city}
              helperText={methods.formState.errors.city?.message}
            />
          </Grid2>
          <Grid2 size={2}>
            <TextField
              fullWidth
              label={t("admin.accounts.account.zip-code")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              sx={{ ...formStyles }}
              {...methods.register("zipCode")}
              error={!!methods.formState.errors.zipCode}
              helperText={methods.formState.errors.zipCode?.message}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing}
              label={t("admin.accounts.account.description")}
              placeholder={t("admin.accounts.account.description-placeholder")}
              multiline
              minRows={6}
              sx={{ ...formStyles }}
              {...methods.register("description")}
              error={!!methods.formState.errors.description}
              helperText={methods.formState.errors.description?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
